/**
 * regionTrend — shared today-vs-typical trend computation.
 *
 * Single source of truth for "how does the current regional people-waiting
 * compare to the typical baseline at the same hour?" — one deadband and one
 * closest-typical helper, shared by:
 *   - OverviewPage hero pill
 *   - RegionTrendChart callout annotation
 */

// ── Types ──────────────────────────────────────────────────────────────────────

// Point shapes are owned by regionChart.ts (single source of truth); re-exported
// here so existing importers of regionTrend keep working.
import type { TodayPoint, TypicalPoint } from "./regionChart";
export type { TodayPoint, TypicalPoint } from "./regionChart";

export type TrendDirection = "up" | "down" | "norm" | "noData";

export interface TrendResult {
  direction: TrendDirection;
  /** Rounded integer percent vs. typical avg. Null when no baseline. */
  pct: number | null;
  /** The matching typical avg value used for comparison. Null when unavailable. */
  typicalAvg: number | null;
}

// ── Constants ──────────────────────────────────────────────────────────────────

/** ±5% deadband — "in norma" for differences within this range. */
const DEADBAND_PCT = 5;

// ── Implementation ─────────────────────────────────────────────────────────────

/**
 * Find the typical point whose hour is closest to the given hour.
 * Returns undefined when `typical` is empty.
 */
function closestTypicalPoint(
  typical: readonly TypicalPoint[],
  hour: number,
): TypicalPoint | undefined {
  return typical.reduce<TypicalPoint | undefined>((best, p) => {
    if (best === undefined) return p;
    return Math.abs(p.hourLocal - hour) < Math.abs(best.hourLocal - hour) ? p : best;
  }, undefined);
}

/**
 * Compute the trend of today's last data point vs. the typical baseline.
 *
 * Guards:
 *  - `today` empty or last point has `waiting === null` → `noData`
 *  - no typical with a non-null `avg` → `noData`
 *  - `typicalAvg === 0` → divide-by-zero guard → `noData`
 */
export function regionTrend(
  today: readonly TodayPoint[],
  typical: readonly TypicalPoint[],
): TrendResult {
  // Find last non-null today point
  const lastToday = [...today].reverse().find((p) => p.waiting !== null);
  if (lastToday === undefined || lastToday.waiting === null) {
    return { direction: "noData", pct: null, typicalAvg: null };
  }

  const match = closestTypicalPoint(typical, lastToday.hourLocal);
  if (match === undefined || match.avg === null || match.avg === 0) {
    return { direction: "noData", pct: null, typicalAvg: null };
  }

  const rawPct = ((lastToday.waiting - match.avg) / match.avg) * 100;
  const pct = Math.round(rawPct);

  let direction: TrendDirection;
  if (rawPct > DEADBAND_PCT) {
    direction = "up";
  } else if (rawPct < -DEADBAND_PCT) {
    direction = "down";
  } else {
    direction = "norm";
  }

  return { direction, pct, typicalAvg: match.avg };
}
